import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Typography } from '../../ui';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable as DataTable } from '../../components/TwDataTable';
import type { DataTableColumn } from '../../components/TwDataTable';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function OutstandingAgingPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['aging'],
    queryFn: () => request<any[]>({ url: '/outstanding/aging' }),
  });

  // Resolve retailerId → name (gated; falls back to a row name field or #id).
  const canViewRetailers = usePermission('RETAILER.VIEW');
  const { data: retailersResp } = useQuery({
    queryKey: ['aging-retailers'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 1000 } }),
    enabled: canViewRetailers,
  });
  const retailerMap = useMemo(() => {
    const m = new Map<string, any>();
    for (const r of retailersResp?.data ?? []) m.set(String(r.id), r);
    return m;
  }, [retailersResp]);

  const columns: DataTableColumn<any>[] = [
    {
      key: 'retailerId', title: 'Retailer', dataIndex: 'retailerId', sortable: true, alwaysVisible: true,
      render: (v: any, row: any) => {
        const r = retailerMap.get(String(v));
        return r ? `${r.companyName} (${r.code})` : (row.companyName ?? `#${v}`);
      },
    },
    { key: 'b0_30',  title: '0-30',  dataIndex: 'b0_30',  sortable: true, align: 'right', render: inr2 },
    { key: 'b31_60', title: '31-60', dataIndex: 'b31_60', sortable: true, align: 'right', render: inr2 },
    { key: 'b61_90', title: '61-90', dataIndex: 'b61_90', sortable: true, align: 'right', render: inr2 },
    {
      key: 'b_gt90', title: '>90', dataIndex: 'b_gt90', sortable: true, align: 'right',
      render: (v: any) => <span style={{ color: Number(v) > 0 ? '#D9363E' : undefined }}>{inr2(v)}</span>,
    },
    {
      key: 'total', title: 'Total', dataIndex: 'total', sortable: true, align: 'right',
      render: (v: any) => <b>{inr2(v)}</b>,
    },
  ];

  return (
    <div>
      <Typography.Title level={4}>Outstanding Aging</Typography.Title>
      <Card>
        <DataTable
          storageKey="aging"
          rowKey="retailerId"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true }}
        />
      </Card>
    </div>
  );
}
